import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Circle, Eraser, Minus, PenLine, Square, Trash2, Undo2, X } from 'lucide-react';

type Pt = { x: number; y: number };

interface Stroke {
  tool: 'pen' | 'eraser' | 'line' | 'rect' | 'ellipse';
  color: string;
  width: number;
  points: Pt[];
}

interface DrawingPadProps {
  onCancel: () => void;
  onSave: (dataUrl: string) => void;
}

const CANVAS_W = 880;
const CANVAS_H = 480;
const COLORS = ['#1f2937', '#d64545', '#2fa39b', '#d99a1f', '#3564d6', '#ffffff'];
const WIDTHS = [2, 4, 8];

/** 画板：自由绘制 / 直线 / 矩形 / 椭圆 / 橡皮，导出 PNG 插入正文 */
export default function DrawingPad({ onCancel, onSave }: DrawingPadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tool, setTool] = useState<Stroke['tool']>('pen');
  const [color, setColor] = useState(COLORS[0]);
  const [width, setWidth] = useState(WIDTHS[1]);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [redoStack, setRedoStack] = useState<Stroke[]>([]);
  const drawingRef = useRef<Stroke | null>(null);
  const [draftTick, setDraftTick] = useState(0);

  // 重绘所有笔画 + 当前正在绘制的一条
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!context) return;

    const paintStroke = (stroke: Stroke) => {
      context.globalCompositeOperation =
        stroke.tool === 'eraser' ? 'destination-out' : 'source-over';
      context.strokeStyle = stroke.color;
      context.fillStyle = stroke.color;
      context.lineWidth = stroke.width;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.beginPath();
      if (stroke.tool === 'pen' || stroke.tool === 'eraser') {
        stroke.points.forEach((point, index) => {
          if (index === 0) context.moveTo(point.x, point.y);
          else context.lineTo(point.x, point.y);
        });
        if (stroke.points.length === 1) {
          context.arc(stroke.points[0].x, stroke.points[0].y, stroke.width / 2, 0, Math.PI * 2);
          context.fill();
          return;
        }
        context.stroke();
        return;
      }
      const [start, end] = [stroke.points[0], stroke.points[stroke.points.length - 1]];
      if (stroke.tool === 'line') {
        context.moveTo(start.x, start.y);
        context.lineTo(end.x, end.y);
        context.stroke();
      } else if (stroke.tool === 'rect') {
        context.strokeRect(
          Math.min(start.x, end.x),
          Math.min(start.y, end.y),
          Math.abs(end.x - start.x),
          Math.abs(end.y - start.y),
        );
      } else {
        const cx = (start.x + end.x) / 2;
        const cy = (start.y + end.y) / 2;
        context.ellipse(
          cx,
          cy,
          Math.abs(end.x - start.x) / 2,
          Math.abs(end.y - start.y) / 2,
          0,
          0,
          Math.PI * 2,
        );
        context.stroke();
      }
    };

    context.clearRect(0, 0, CANVAS_W, CANVAS_H);
    strokes.forEach(paintStroke);
    if (drawingRef.current) paintStroke(drawingRef.current);
  }, [strokes, draftTick]);

  const toCanvasPoint = (event: React.PointerEvent<HTMLCanvasElement>): Pt => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * CANVAS_W,
      y: ((event.clientY - rect.top) / rect.height) * CANVAS_H,
    };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    drawingRef.current = { tool, color, width, points: [toCanvasPoint(event)] };
    setRedoStack([]);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const stroke = drawingRef.current;
    if (!stroke) return;
    stroke.points.push(toCanvasPoint(event));
    setDraftTick((tick) => tick + 1);
  };

  const handlePointerUp = () => {
    const stroke = drawingRef.current;
    if (!stroke) return;
    drawingRef.current = null;
    if (stroke.points.length > 0) setStrokes((list) => [...list, stroke]);
  };

  const handleUndo = () => {
    setStrokes((list) => {
      if (list.length === 0) return list;
      const last = list[list.length - 1];
      setRedoStack((redo) => [...redo, last]);
      return list.slice(0, -1);
    });
  };

  const handleRedo = () => {
    setRedoStack((redo) => {
      if (redo.length === 0) return redo;
      const last = redo[redo.length - 1];
      setStrokes((list) => [...list, last]);
      return redo.slice(0, -1);
    });
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // 白底合成，避免透明背景在正文中显示异常
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = CANVAS_W;
    exportCanvas.height = CANVAS_H;
    const context = exportCanvas.getContext('2d');
    if (!context) return;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, CANVAS_W, CANVAS_H);
    context.drawImage(canvas, 0, 0);
    onSave(exportCanvas.toDataURL('image/png'));
  };

  const ToolButton = ({
    target,
    title,
    children,
  }: {
    target: Stroke['tool'];
    title: string;
    children: ReactNode;
  }) => (
    <button
      type="button"
      className={`rt-btn ${tool === target ? 'rt-btn-active' : ''}`}
      title={title}
      aria-label={title}
      onClick={() => setTool(target)}
    >
      {children}
    </button>
  );

  return (
    <div className="rt-modal-mask" role="dialog" aria-modal="true" aria-label="手绘插图">
      <div className="rt-modal">
        <div className="rt-modal-header">
          <strong>手绘插图</strong>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} aria-label="关闭画板">
            <X size={16} />
          </button>
        </div>
        <div className="rt-modal-tools">
          <div className="rt-group">
            <ToolButton target="pen" title="画笔">
              <PenLine size={15} />
            </ToolButton>
            <ToolButton target="line" title="直线">
              <Minus size={15} />
            </ToolButton>
            <ToolButton target="rect" title="矩形">
              <Square size={15} />
            </ToolButton>
            <ToolButton target="ellipse" title="椭圆">
              <Circle size={15} />
            </ToolButton>
            <ToolButton target="eraser" title="橡皮擦">
              <Eraser size={15} />
            </ToolButton>
          </div>
          <span className="rt-sep" aria-hidden="true" />
          <div className="rt-group rt-colors">
            {COLORS.map((item) => (
              <button
                key={item}
                type="button"
                className={`rt-color ${color === item ? 'rt-color-active' : ''}`}
                style={{ background: item }}
                title={item}
                aria-label={`选择颜色 ${item}`}
                onClick={() => setColor(item)}
              />
            ))}
            <input
              type="color"
              value={color}
              title="自定义颜色"
              aria-label="自定义颜色"
              onChange={(event) => setColor(event.target.value)}
            />
          </div>
          <span className="rt-sep" aria-hidden="true" />
          <div className="rt-group">
            {WIDTHS.map((item) => (
              <button
                key={item}
                type="button"
                className={`rt-btn ${width === item ? 'rt-btn-active' : ''}`}
                title={`线条粗细 ${item}`}
                aria-label={`线条粗细 ${item}`}
                onClick={() => setWidth(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <span className="rt-sep" aria-hidden="true" />
          <div className="rt-group">
            <button
              type="button"
              className="rt-btn"
              title="撤销"
              aria-label="撤销"
              disabled={strokes.length === 0}
              onClick={handleUndo}
            >
              <Undo2 size={15} />
            </button>
            <button
              type="button"
              className="rt-btn"
              title="重做"
              aria-label="重做"
              disabled={redoStack.length === 0}
              onClick={handleRedo}
            >
              <Undo2 size={15} style={{ transform: 'scaleX(-1)' }} />
            </button>
            <button
              type="button"
              className="rt-btn"
              title="清空画布"
              aria-label="清空画布"
              onClick={() => {
                setStrokes([]);
                setRedoStack([]);
              }}
            >
              <Trash2 size={15} />
            </button>
          </div>
        </div>
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          className="rt-canvas"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />
        <div className="rt-modal-footer">
          <button type="button" className="btn btn-outline btn-sm" onClick={onCancel}>
            取消
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleSave}>
            插入正文
          </button>
        </div>
      </div>
    </div>
  );
}
